import { PluginListenerHandle } from '@capacitor/core';
import {
  format,
  isAfter, isBefore, isEqual, isValid, parseISO, roundToNearestMinutes,
} from 'date-fns';

export type DateCompareType = 'before' | 'beforeEqual' | 'after' | 'afterEqual' | 'equal';

export interface GeoPoint {
  lat: number;
  lng: number;
}

const MIN_SKELETON_WORD_LENGTH = 3;
const MAX_SKELETON_WORD_LENGTH = 10;
const MAX_SKELETON_TEXT_LENGTH = 50;
const SKELETON_VALUE: { [key: string]: { min: number; max: number } } = {
  canonicalAddress: {
    max: 80,
    min: 30,
  },
  description: {
    max: 300,
    min: 100,
  },
  title: {
    max: 20,
    min: 10,
  },
};

// eslint-disable-next-line max-statements
export function fillSkeletonData(key: string, symbol: string = 'x'): string {
  let minTextLength: number = 0;
  let maxTextLength: number = MAX_SKELETON_TEXT_LENGTH;
  let skeletonText: string = '';

  if (SKELETON_VALUE[key]) {
    minTextLength = SKELETON_VALUE[key].min;
    maxTextLength = SKELETON_VALUE[key].max;
  }

  let textLength = maxTextLength;

  if (minTextLength) {
    textLength = Math.floor(Math.random() * (maxTextLength - minTextLength + 1)) + minTextLength;
  }

  // if textLength doesn't exceed max length,
  // just return the text length filled with `symbol`
  if (textLength < MAX_SKELETON_TEXT_LENGTH) {
    skeletonText = Array(textLength).fill(symbol)
      .join('');

    return skeletonText;
  }

  // if textLength exceeds max length,
  // add white space after every word (example output: "xxx xxxxxxx xxxxx xxx xxxxxxxx")
  // this ensures proper line breaks for multiline text
  let randomWordLength = MIN_SKELETON_WORD_LENGTH;
  let charCounter = 0;

  for (let i = 0; i < textLength; i++) {
    if (charCounter < randomWordLength) {
      skeletonText += symbol;
      charCounter += 1;
    } else {
      skeletonText += ' ';
      charCounter = 0;
      randomWordLength = Math.floor(Math.random()
        * (MAX_SKELETON_WORD_LENGTH - MIN_SKELETON_WORD_LENGTH + 1)) + MIN_SKELETON_WORD_LENGTH;
    }
  }

  return skeletonText;
}

export function unbindPluginListeners(pluginListeners: { [key: string]: PluginListenerHandle }): void {
  if (!pluginListeners || !Object.keys(pluginListeners).length) {
    return;
  }

  try {
    for (const key of Object.keys(pluginListeners)) {
      console.info('unbind plugin listener', key);
      pluginListeners[key].remove().catch();
    }
  } catch (error) {
    console.error(error.message);
  }
}

export function sortByOrderKeys<T, U>(items: T[], propertyKey: string, order: U[]): T[] {
  return items.sort((a, b) => {
    return order.indexOf(a[propertyKey]) - order.indexOf(b[propertyKey]);
  });
}

// simple check for value being in HH:mm format
export function isTimeFormat(value): boolean {
  const TIME_FORMAT_LENGTH = 5;

  return value
    ? value.length === TIME_FORMAT_LENGTH && value.includes(':')
    : false;
}

// HH:mm to YYYY-MM-DDTHH:mm
export function getIsoDateFromTimeValue(value: string): string {
  let currentDate = new Date();

  const time = value
    .split(':')
    .map((v) => { return Number(v); });

  currentDate.setHours(time[0], time[1]);
  currentDate = roundToNearestMinutes(currentDate, {
    roundingMethod: 'floor',
  });

  return format(currentDate, 'yyyy-MM-dd\'T\'HH:mm');
}

// valid ISO 8601 dates:
// Year             YYYY
// Year and Month   YYYY-MM
// Complete Date    YYYY-MM-DD
// Date and Time    YYYY-MM-DDTHH:mm
// UTC Timezone     YYYY-MM-DDTHH:mm:ssZ
// Timezone Offset  YYYY-MM-DDTHH:mm:ssTZD
export function dateIsValid(isoDateString1: string, isoDateString2?: string, compareType?: DateCompareType): boolean {
  const date1 = parseISO(isTimeFormat(isoDateString1)
    ? getIsoDateFromTimeValue(isoDateString1)
    : isoDateString1);

  // no second date provided -> only check if first date is valid iso string
  if (!isoDateString2) {
    return isValid(date1);
  }

  const date2 = parseISO(isTimeFormat(isoDateString2)
    ? getIsoDateFromTimeValue(isoDateString2)
    : isoDateString2);

  // second date is set but is no valid iso string
  if (!isValid(date2)) {
    return false;
  }

  let isValidDate: boolean;

  switch (compareType) {
    case 'before':
      isValidDate = isBefore(date1, date2);
      break;

    case 'beforeEqual':
      isValidDate = isBefore(date1, date2) || isEqual(date1, date2);
      break;

    case 'after':
      isValidDate = isAfter(date1, date2);
      break;

    case 'afterEqual':
      isValidDate = isAfter(date1, date2) || isEqual(date1, date2);
      break;

    default:
      isValidDate = isEqual(date1, date2);
  }

  return isValidDate;
}

export function isEmpty(value: unknown): boolean {
  // eslint-disable-next-line no-undefined
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }

  // All other values (numbers, booleans, functions, symbols, etc.) are not empty
  return false;
}
